import { DeviceTypeIds, NwkRoute, NL4Read, FirmwareUpdate } from '@ncss/models/dist/v3';
import { NL4, NL4InfoMessageType, NL4MessageType, ConversionUtils } from '@ncss/models';
import { BLE_SERVICES, DirectConnectBaseDevice, VisibleDevice } from '../base/directConnectBaseDevice';
import { ByteList } from 'byte-list';
import { NL4BLEDeviceMessage } from './messages/NL4BLEDeviceMessage';
import { NL4BLERouteMessage, NL4BLERouteMessageType } from './messages/NL4BLERouteMessage';
import { computed, ComputedRef, Ref, ref, shallowRef, ShallowRef } from 'vue';
import { NL4BLEInfoMessage } from './messages/NL4BLEInfoMessage';
import { NcssEncryptionVersion } from 'ncss-encryption';
import { NL4BLERouteRequestMessage, NL4BLERouteRequestMessageType } from './messages/NL4BLERouteRequestMessage';
import { BitwiseHelper } from '@ncss/message';
import { NL4UpdateMessage, NL4UpdateMessageType, UPDATE_DATA_SIZE } from './messages/NL4UpdateMessage';
import { NL4UpdateStatusMessage, NL4UpdateStatusMessageType, UpdateError } from './messages/NL4UpdateStatusMessage';

export enum NL4UpdateType {
  MCU = 0,
  RADIO = 1,
  BLE_APP = 3,
}

const UpdateTypeNames: { [key in NL4UpdateType]: string } = {
  [NL4UpdateType.MCU]: 'NextLink',
  [NL4UpdateType.BLE_APP]: 'NextLink Bluetooth Components',
  [NL4UpdateType.RADIO]: 'NextLink Radio Components',
};

interface FirmwareBlock {
  blockId: number;
  block: Uint8Array;
}

const UpdateMsgTypeMap: { [key in NL4UpdateType]: NL4UpdateMessageType } = {
  [NL4UpdateType.MCU]: NL4UpdateMessageType.MCU,
  [NL4UpdateType.BLE_APP]: NL4UpdateMessageType.BLE,
  [NL4UpdateType.RADIO]: NL4UpdateMessageType.RADIO,
};

const UpdateDeviceTypeMap: { [key in NL4UpdateType]: DeviceTypeIds } = {
  [NL4UpdateType.MCU]: DeviceTypeIds.NL4,
  [NL4UpdateType.BLE_APP]: DeviceTypeIds.NRF_APP,
  [NL4UpdateType.RADIO]: DeviceTypeIds.NL4,
};

const PostUpdateDelays: { [key in NL4UpdateType]: number } = {
  [NL4UpdateType.MCU]: 6000,
  [NL4UpdateType.BLE_APP]: 4000,
  [NL4UpdateType.RADIO]: 8000,
};

export class DirectConnectNL4 extends DirectConnectBaseDevice {
  static Init() {
    DirectConnectBaseDevice.RegisterType(DeviceTypeIds.NL4, DirectConnectNL4);
  }

  get loading() {
    return this._loading.value;
  }

  get signalStrengthStr() {
    return this._linkQualityStr.value;
  }

  get hardwareStr() {
    if (this.info) {
      return `v${this.info.hardwareVersionMajor}.${this.info.hardwareVersionMinor}`;
    }
    return '';
  }

  get firmwareStr() {
    if (this.info) {
      return `v${this.info.firmwareVersionMajor}.${this.info.firmwareVersionMinor}`;
    }
    return '';
  }

  get showOrientationWarning() {
    if (this.info) {
      const heading = this.info.headingDegrees;
      const facingSouth = heading >= 90 && heading <= 270;
      return !facingSouth;
    }
    return false;
  }

  get info() {
    return this._info.value;
  }

  get read() {
    return this._read.value;
  }

  get temperatureStr() {
    if (this.useMetric) {
      const val = this.info?.temperature;
      return (val || val === 0 ? val : '-') + ' C';
    } else {
      const val = this.info?.temperature;
      return (val || val === 0 ? ConversionUtils.ConvertCelsiusToFahrenheit(val)?.toFixed(1) : '-') + ' F';
    }
  }

  get rapidCheckInEnabled() {
    return !!this.info?.rapidCheckInEnabled;
  }

  get batteryPercentage() {
    return (this.info?.batteryPercentage?.toString() || '-') + '%';
  }
  get hasSun() {
    return this.info?.hasSun;
  }
  get isCharging() {
    return !!this.info?.isCharging;
  }
  get pullingRouteTable() {
    return !!this._pullingRouteTable.value;
  }
  get routeCount() {
    return this._routeCount.value;
  }

  protected serviceId = BLE_SERVICES.WALK_BY.ID;
  protected notifyCharacteristicId = BLE_SERVICES.WALK_BY.CHARACTERISTICS.NOTIFY;
  protected writeCharacteristicId = BLE_SERVICES.WALK_BY.CHARACTERISTICS.WRITE;
  protected useEncryption = true;
  protected encryptionKey = NcssEncryptionVersion.NL4;

  private _pullingRouteTable = ref(false);
  private _routeCount = ref(0);
  private _loading: ShallowRef<boolean>;
  private _info: ShallowRef<NL4 | undefined>;
  private _read: ComputedRef<NL4Read | undefined>;
  private _rfSignalStrength: ShallowRef<number | undefined>;
  private _linkQualityStr: ComputedRef<string>;
  private _requestInfoInterval?: NodeJS.Timeout;

  constructor(visibleDevice: VisibleDevice, useMetric: boolean) {
    super(visibleDevice, useMetric);
    this._loading = shallowRef(true);
    this._info = shallowRef();
    this._read = computed(() => {
      if (this._info.value) {
        return NL4Read.Create(visibleDevice.deviceId, this._info.value, {
          firstHopId: 0,
          firstHopRssi: 0,
          lastHopRssi: 0,
          isProgrammed: false,
        });
      }
      return undefined;
    });
    this._rfSignalStrength = shallowRef();
    this._linkQualityStr = computed(() => this.computeLinkQualityStr(this._rfSignalStrength.value || 0));
  }

  protected parseManufacturerData(bytes: ByteList): void {
    try {
      bytes.readByte(); // batteryPercentage
      bytes.readInt8(); // TemperatureC
      const manufacturingFlags = bytes.readUInt16();
      this._rfSignalStrength.value = BitwiseHelper.GetBits(manufacturingFlags, 0, 3);
    } catch (e) {
      console.log('Error Parsing NL4 Man Data', bytes.toString(), bytes.index);
    }
  }

  protected onConnected(): void {
    super.onConnected();
    clearInterval(this._requestInfoInterval);
    this._requestInfoInterval = setInterval(() => this.requestInfo(), 2000);
  }

  protected onDisconnected(): void {
    super.onDisconnected();
    clearInterval(this._requestInfoInterval);
  }

  protected registerMessages(): void {
    this.messageService.registerMessageGroups([
      {
        mask: NL4MessageType.GROUP_MASK,
        messageClass: NL4BLEDeviceMessage,
        handler: this.onDeviceMessage.bind(this),
      },
      {
        mask: NL4BLERouteMessageType.GROUP_MASK,
        messageClass: NL4BLERouteMessage,
        handler: this.onRouteMessage.bind(this),
      },
      {
        mask: NL4UpdateStatusMessageType.GROUP_MASK,
        messageClass: NL4UpdateStatusMessage,
        handler: this.onUpdateStatusMessage.bind(this),
      },
    ]);
  }

  async setRapidCheckIn(enabled: boolean) {
    const msg = new NL4BLEInfoMessage(this.deviceId, NL4InfoMessageType.SET_INFO);
    if (enabled) {
      msg.nl4Info.startRapidCheckIn = true;
    } else {
      msg.nl4Info.stopRapidCheckIn = true;
    }
    const res = await this.sendMessage<NL4BLEDeviceMessage>(msg, {
      match: (res) => res.nl4?.rapidCheckInEnabled === enabled,
    });
    return !!res;
  }

  async pullRouteTable() {
    this._routeCount.value = 0;
    this._pullingRouteTable.value = true;
    const routes: NwkRoute[] = [];
    let page = 1;
    let keepGoing = true;
    while (keepGoing) {
      const msg = new NL4BLERouteRequestMessage(this.deviceId);
      msg.startingPage = page;
      const res = await this.sendMessage<NL4BLERouteMessage>(msg, {
        match: (res) => !!res && res instanceof NL4BLERouteMessage,
      });
      if (!res) {
        keepGoing = false;
      } else {
        if (res?.routes?.length) {
          routes.push(...res.routes);
          this._routeCount.value = routes.length;
          page++;
        } else {
          keepGoing = false;
        }
      }
    }
    this._pullingRouteTable.value = false;
    return routes;
  }

  async sortRouteTable(): Promise<boolean> {
    const msg = new NL4BLERouteRequestMessage(this.deviceId);
    msg.sortTable = true;
    const res = await this.sendMessage<NL4BLERouteMessage>(msg, {
      match: (res) => !!res && res instanceof NL4BLERouteMessage && res.isSorting,
    });
    return !!res;
  }

  async dropRouteTable(): Promise<void> {
    const msg = new NL4BLERouteRequestMessage(this.deviceId);
    msg.dropTable = true;
    await this.sendMessage<NL4BLERouteMessage>(msg);
  }

  private requestInfo() {
    const msg = new NL4BLEInfoMessage(this.deviceId);
    return this.sendMessage<NL4BLEDeviceMessage>(msg, { match: (res) => !!res });
  }

  private onDeviceMessage(msg: NL4BLEDeviceMessage) {
    this._loading.value = false;
    if (!msg.nl4) {
      return;
    }
    this._info.value = msg.nl4;
    this.checkResponse(msg);
  }

  private onRouteMessage(msg: NL4BLERouteMessage) {
    this.checkResponse(msg);
  }

  private onUpdateStatusMessage(msg: NL4UpdateStatusMessage) {
    this.checkResponse(msg);
  }

  private computeLinkQualityStr(rfSignal: number) {
    switch (rfSignal) {
      case 4:
        return 'Excellent';
      case 3:
      case 2:
        return 'Good';
      case 1:
        return 'Poor';
      default:
        return '';
    }
  }

  async applyUpdate(
    update: FirmwareUpdate,
    progressRef: Ref<number | undefined>,
    progressMessageRef: Ref<string | undefined>
  ) {
    clearInterval(this._requestInfoInterval);
    clearTimeout(this.periodicChecksTimeout);
    const msgType = UpdateMsgTypeMap[update.firmwareType as NL4UpdateType];
    const transferId = Math.floor(Math.random() * 99) + 1;
    let index = 0;
    const blocks = this.getUpdateChunks(update);
    let success = true;
    let error: string | undefined = undefined;
    progressMessageRef.value = `Updating ${UpdateTypeNames[update.firmwareType as NL4UpdateType]}...`;
    while (index < blocks.length) {
      const msg = new NL4UpdateMessage(msgType);
      msg.transferId = transferId;
      msg.swd = update.firmwareType !== NL4UpdateType.MCU;
      msg.firmwareVersionMajor = update.version.major;
      msg.firmwareVersionMinor = update.version.minor;
      msg.totalBlocks = blocks.length;
      msg.currentBlock = index + 1;
      msg.deviceTypeId = UpdateDeviceTypeMap[update.firmwareType as NL4UpdateType];
      msg.dataBlock = blocks[index].block;
      progressRef.value = msg.currentBlock / msg.totalBlocks;
      if (msg.currentBlock === msg.totalBlocks && update.firmwareType === NL4UpdateType.BLE_APP) {
        await this.sendMessage(msg);
        ++index;
        continue;
      }
      const res = await this.sendMessage<NL4UpdateStatusMessage>(msg, {
        match: (r) => r instanceof NL4UpdateStatusMessage,
        retries: 3,
        timeOutSeconds: 2,
      });
      if (!res) {
        error = 'Timed out while applying update';
        success = false;
        break;
      }
      if (res.error === UpdateError.INFO_CHANGED) {
        index = res.currentBlock - 1;
      } else if (res.error !== UpdateError.NONE) {
        error = 'An error occured while updating';
        success = false;
        break;
      } else {
        ++index;
      }
    }
    progressRef.value = 1;
    if (success) {
      const delayTime = PostUpdateDelays[update.firmwareType as NL4UpdateType];
      await this.disconnect();
      progressMessageRef.value = 'Applying changes...';
      await new Promise<void>((resolve) => setTimeout(resolve, delayTime));
      await this.connect();
    }
    progressRef.value = undefined;
    progressMessageRef.value = undefined;
    return { success, error };
  }

  private getUpdateChunks(update: FirmwareUpdate): FirmwareBlock[] {
    const blocks: FirmwareBlock[] = [];
    const blockCount = update.data.byteLength / UPDATE_DATA_SIZE;
    for (let i = 0, j = 1; i < blockCount; i++, j++) {
      blocks.push({
        blockId: i,
        block: new Uint8Array(update.data.subarray(i * UPDATE_DATA_SIZE, j * UPDATE_DATA_SIZE)),
      });
    }
    return blocks;
  }
}
